import type { DateChipOption } from '@openx/components-core/lib/DateRangePickerField/components/DateRangeChips.tsx';
import type {
  DealInsightsApi,
  DealInsightsCacheKeys,
  DealInsightsChartInputApi,
  DealInsightsCreateJobIdParamsApi,
} from '@openx/types/dealInsights.ts';
import { isSameDay } from 'date-fns';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDateRangeChipOptions } from '../../../DealInsights/hooks/useDateRangeChipOptions.ts';
import { getDealInsightsSupportedDateRange } from '../../../DealInsights/utils/getDealInsightsSupportedDateRange.ts';
import { useDealContext } from '../../context/DealContext.tsx';
import { useDealInsightsCache } from './useDealInsightsCache.ts';
import { extractDataAndLoadingStateFromCache } from './utils/extractDataAndLoadingStateFromCache.ts';

type UseDealInsightsParams = DealInsightsCreateJobIdParamsApi & {
  activeTab?: { name: string; path: string; index: number };
};

const findDatePreset = (startDate: string, endDate: string, chipOptions: DateChipOption[]): string | undefined => {
  const start = new Date(startDate);
  const end = new Date(endDate);

  const matchingOption = chipOptions.find(
    option => isSameDay(option.startDate, start) && isSameDay(option.endDate, end)
  );

  return matchingOption?.label;
};

export const useDealInsights = (params: UseDealInsightsParams) => {
  const { useFetchDealInsights, useGetDealInsightsJobId } = useDealContext();
  const { activeTab, ...dealInsightsParams } = params;
  const { maxDate } = getDealInsightsSupportedDateRange();
  const dateRangeChipOptions = useDateRangeChipOptions(maxDate);

  const [fetchDataTriggerParams, setFetchDataTriggerParams] = useState<DealInsightsCreateJobIdParamsApi>();
  const loadStartTimeRef = useRef<number | null>(null);

  // Stable ref for dealInsightsParams to use in callbacks
  const dealInsightsParamsRef = useRef(dealInsightsParams);
  dealInsightsParamsRef.current = dealInsightsParams;

  const {
    error: jobIdFetchError,
    refetch: refetchJobId,
    data: { job_id, params: jobCreateParams } = {},
  } = useGetDealInsightsJobId({
    payload: fetchDataTriggerParams ? fetchDataTriggerParams : ({} as DealInsightsCreateJobIdParamsApi),
    enabled: !!fetchDataTriggerParams,
  });

  const {
    error: insightsDataFetchError,
    data,
    cleanupJob,
    stopPolling,
    requestsCount,
  } = useFetchDealInsights({
    enabled: !!fetchDataTriggerParams && !!job_id,
    job_id: job_id ?? '',
  });

  const { cache, paramsString, activeChartKeys, getDataFromCache, triggerDataFetch } = useDealInsightsCache({
    params: dealInsightsParams,
    data,
    job_id,
    jobCreateParams,
    setFetchDataTriggerParams,
    cleanupJob,
    stopPolling,
  });

  // Ref to track pending fetch promises for sections
  const pendingFetchResolversRef = useRef<Map<string, { resolve: (data: Partial<DealInsightsApi>) => void }>>(
    new Map()
  );

  // Function to fetch data for specific chart keys (used for PDF export)
  // Returns a promise that resolves when data is available (from cache or after fetching)
  const fetchDataForChartKeys = useCallback(
    (
      chartKeys: DealInsightsCacheKeys[],
      chartInputs: Partial<DealInsightsChartInputApi>
    ): Promise<Partial<DealInsightsApi>> => {
      const cacheResult = getDataFromCache(chartKeys, chartInputs);

      // All data is in cache - return immediately
      if (cacheResult.missingChartKeys.length === 0) {
        return Promise.resolve(cacheResult.data);
      }

      // Some data is missing - trigger fetch and return promise
      return new Promise(resolve => {
        // Store the full list of requested chart keys so that when data arrives
        // we can resolve the promise with a complete data object (cached + newly fetched),
        // instead of only the subset that was missing.
        const fetchKey = JSON.stringify({ chartKeys, chartInputs });
        pendingFetchResolversRef.current.set(fetchKey, { resolve });

        // Build chart config for missing keys only
        const chartsToFetch: DealInsightsChartInputApi = {};
        for (const key of cacheResult.missingChartKeys) {
          // @ts-expect-error - dynamic key assignment is safe here as keys come from DealInsightsChartInputApi
          chartsToFetch[key] = chartInputs[key];
        }

        triggerDataFetch({
          ...dealInsightsParamsRef.current,
          charts: chartsToFetch,
        });
      });
    },
    [getDataFromCache, triggerDataFetch]
  );

  // Resolve pending fetch promises when data arrives
  useEffect(() => {
    if (data && pendingFetchResolversRef.current.size > 0) {
      // Get all pending resolvers and resolve them
      pendingFetchResolversRef.current.forEach(({ resolve }, fetchKey) => {
        const { chartKeys, chartInputs } = JSON.parse(fetchKey);
        const cacheResult = getDataFromCache(chartKeys, chartInputs);

        // Check if all requested data is now available
        if (cacheResult.missingChartKeys.length === 0) {
          resolve(cacheResult.data);
          pendingFetchResolversRef.current.delete(fetchKey);
        }
      });
    }
  }, [data, getDataFromCache]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: activeChartKeys are part of paramsString and not needed in dep array, including them causes infinite re-renders
  const { data: dataToReturn, chartsLoadingState } = useMemo(
    () => extractDataAndLoadingStateFromCache({ cache, paramsString, activeChartKeys }),
    [cache, paramsString]
  );

  const error = jobIdFetchError ?? insightsDataFetchError;

  // Start timer when data loading begins
  useEffect(() => {
    if (fetchDataTriggerParams && !loadStartTimeRef.current) {
      loadStartTimeRef.current = Date.now();
    }
  }, [fetchDataTriggerParams]);

  // Track Pendo event when data is ready
  useEffect(() => {
    if (dataToReturn && loadStartTimeRef.current && window.pendo) {
      const timeToRenderMs = Date.now() - loadStartTimeRef.current;

      // Reset timer for next load
      loadStartTimeRef.current = null;

      // Extract date information from params
      const dateMode = dealInsightsParams.start_date === dealInsightsParams.end_date ? 'custom' : 'preset';
      const datePreset = findDatePreset(
        dealInsightsParams.start_date,
        dealInsightsParams.end_date,
        dateRangeChipOptions
      );

      window.pendo.track('DealInsights_data_ready', {
        tab: activeTab?.name || 'General',
        date_mode: dateMode,
        date_preset: datePreset,
        date_start: dealInsightsParams.start_date,
        date_end: dealInsightsParams.end_date,
        time_to_render_ms: timeToRenderMs,
        bq_queries: requestsCount,
        error: !!error,
      });
    }
  }, [
    dataToReturn,
    dealInsightsParams.start_date,
    dealInsightsParams.end_date,
    error,
    activeTab?.name,
    requestsCount,
    dateRangeChipOptions,
  ]);

  return {
    data: dataToReturn,
    error,
    chartsLoadingState,
    refetch: refetchJobId,
    fetchDataForChartKeys,
    getDataFromCache,
  };
};
